import { AuthenticationMessageHandler } from './auth-message-handler';
import { DeepLinkMessageHandler, LegacyLinkRouter } from './deep-link-message-handler';
import { HostedWebContentSdkService } from '../hosted-web-content-sdk';
import { IMetaOsHubServices } from '../interfaces';
import { IMessageHandler } from './messaging-infra.interface';
import { AppMessageHandler } from './app-message-handler';
import { RegisterHandlerMessageHandler } from './register-handler-message-handler';
import { CalendarLegacyLinkHandler, CalendarMessageHandler } from './calendar-message-handler';
import { MailMessageHandler } from './mail-message-handler';
import { LocationMessageHandler } from './location-message-handler';
import { NotificationMessageHandler } from './notification-message-handler';
import { PageMessageHandler } from './page-message-handler';
import { PageTabsMessageHandler } from './page-tabs-message-handler';
import { MeetingMessageHandler } from './meeting-message-handler';
import { PageConfigMessageHandler } from './page-config-message-handler';
import { BaseMessageProcessor } from './base-message-processor';
import { PageBackStackMessageHandler } from './pages-backstack-message-handler';
import { DialogsMessageHandler } from './dialogs-message-handler';
import { MediaMessageHandler } from './media-message-handler';
import { HubSdkTelemetryService } from '../telemetry-service';
import { ChatMessageHandler } from './chat-message-handler';
import { PagesFullTrustMessageHandler } from './pages-fulltrust-message-handler';
import { TeamsFullTrustMessageHandler } from './teams-fulltrust-message-handler';
import { FilesMessageHandler } from './files-message-handler';
import { LogMessageHandler } from './log-message-handler';
import { PeopleMessageHandler } from './people-message-handler';
import { AppInstallDialogLegacyLinkHandler, AppInstallDialogMessageHandler } from './appInstallDialog-message-handler';
import { CallLegacyLinkHandler, CallMessageHandler } from './call-message-handler';
import { AppEntityMessageHandler } from './appentity-message-handler';

/**
 * MessageProcessor for processing the message requests which involves :
 * Routing message requests to proper handler
 * Posting response back to the app
 */
export class MessageProcessor extends BaseMessageProcessor {
  public messageHandlers: IMessageHandler[] = [];

  /**
   * Registers different available handlers
   */
  constructor(
    protected appSdkHost: HostedWebContentSdkService,
    protected hubServices: IMetaOsHubServices,
    protected telemetryService?: HubSdkTelemetryService
  ) {
    super(appSdkHost);

    const legacyLinkRouter = new LegacyLinkRouter([
      new AppInstallDialogLegacyLinkHandler(hubServices.appInstallDialogService),
      new CalendarLegacyLinkHandler(hubServices.calendarService),
      new CallLegacyLinkHandler(hubServices.callService),
    ]);

    this.messageHandlers.push(
      new AppInstallDialogMessageHandler(appSdkHost, hubServices.appInstallDialogService, telemetryService)
    );
    this.messageHandlers.push(
      new AuthenticationMessageHandler(appSdkHost, hubServices.authenticationService, telemetryService)
    );
    this.messageHandlers.push(
      new DeepLinkMessageHandler(appSdkHost, hubServices.linkService, legacyLinkRouter, telemetryService)
    );
    this.messageHandlers.push(new AppMessageHandler(appSdkHost, hubServices, telemetryService));
    this.messageHandlers.push(new CalendarMessageHandler(appSdkHost, hubServices.calendarService, telemetryService));
    this.messageHandlers.push(new LocationMessageHandler(appSdkHost, hubServices.locationService, telemetryService));
    this.messageHandlers.push(
      new RegisterHandlerMessageHandler(appSdkHost, hubServices.pageService, hubServices.logService, telemetryService)
    );
    this.messageHandlers.push(new MailMessageHandler(appSdkHost, hubServices.mailService, telemetryService));
    this.messageHandlers.push(new MeetingMessageHandler(appSdkHost, hubServices.meetingService, telemetryService));
    this.messageHandlers.push(
      new NotificationMessageHandler(appSdkHost, hubServices.notificationService, telemetryService)
    );
    this.messageHandlers.push(new PageMessageHandler(appSdkHost, hubServices.pageService, telemetryService));
    this.messageHandlers.push(
      new PageTabsMessageHandler(appSdkHost, hubServices.pageService?.tabsService, telemetryService)
    );
    this.messageHandlers.push(
      new PageConfigMessageHandler(appSdkHost, hubServices.pageService?.configService, telemetryService)
    );
    this.messageHandlers.push(new MediaMessageHandler(appSdkHost, hubServices.mediaService, telemetryService));
    this.messageHandlers.push(new ChatMessageHandler(appSdkHost, hubServices.chatService, telemetryService));
    this.messageHandlers.push(
      new PagesFullTrustMessageHandler(appSdkHost, hubServices.pageService?.fullTrustService, telemetryService)
    );
    this.messageHandlers.push(new PageBackStackMessageHandler(appSdkHost, hubServices.pageService?.backStackService));
    this.messageHandlers.push(
      new TeamsFullTrustMessageHandler(appSdkHost, hubServices.teamsService?.fullTrustService, telemetryService)
    );
    this.messageHandlers.push(new DialogsMessageHandler(appSdkHost, hubServices.dialogService, telemetryService));
    this.messageHandlers.push(new FilesMessageHandler(appSdkHost, hubServices.filesService, telemetryService));
    this.messageHandlers.push(new LogMessageHandler(appSdkHost, hubServices.logService, telemetryService));
    this.messageHandlers.push(new PeopleMessageHandler(appSdkHost, hubServices.peopleService, telemetryService));
    this.messageHandlers.push(new CallMessageHandler(appSdkHost, hubServices.callService, telemetryService));
    this.messageHandlers.push(new AppEntityMessageHandler(appSdkHost, hubServices.appEntityService, telemetryService));
    this.messageHandlers.forEach((handler) => handler.initHandlers());
  }
}
